import type { DeviceId } from '@glyph/protocol-ts';
import type { AudioSession, AudioSessionState, CommandClient, Unsubscribe } from './types';
import { newSessionId } from './types';

type Frame = { seq: number; tsMs: number; opus: Uint8Array };

/**
 * Transport side of a listen session: starts the device's Opus monitor (cmd `audio.listen.start`
 * locally, `POST /listen` on the cloud so the gateway can refcount listeners), subscribes to the
 * binary audio frames and hands them to whoever plays them (see audio/player.ts).
 */
export class AudioSessionImpl implements AudioSession {
  state: AudioSessionState = 'starting';
  bufferMs = 120;
  level = -60;
  private frameCbs = new Set<(f: Frame) => void>();
  private stateCbs = new Set<(s: AudioSessionState) => void>();
  private unsub: Unsubscribe | null = null;
  private readonly sessionId = newSessionId();
  private lastFrameAt = 0;
  private stallTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly client: CommandClient,
    readonly deviceId: DeviceId,
    private readonly hooks?: { start: () => Promise<unknown>; stop: () => Promise<unknown> },
  ) {
    void this.start();
  }

  private setState(s: AudioSessionState) {
    if (this.state === s) return;
    this.state = s;
    for (const cb of this.stateCbs) cb(s);
  }

  private async start() {
    try {
      this.unsub = this.client.subscribe(this.deviceId, ['audio_opus'], (ev) => {
        if (ev.topic === '$binary' && ev.data.kind === 'audio') {
          this.lastFrameAt = Date.now();
          if (this.state !== 'playing') this.setState('playing');
          for (const cb of this.frameCbs) cb({ seq: ev.data.seq, tsMs: ev.data.tsMs, opus: ev.data.opus });
        }
      });
      if (this.hooks) await this.hooks.start();
      else await this.client.cmd(this.deviceId, 'audio.listen.start', { session_id: this.sessionId, ttl_s: 900, bitrate_kbps: 24 });
      this.stallTimer = setInterval(() => {
        if (this.state === 'playing' && Date.now() - this.lastFrameAt > 1500) this.setState('stalled');
      }, 500);
    } catch (e) {
      console.error('[listen] start failed', e);
      this.setState('error');
    }
  }

  onFrame(cb: (f: Frame) => void): Unsubscribe {
    this.frameCbs.add(cb);
    return () => this.frameCbs.delete(cb);
  }
  onState(cb: (s: AudioSessionState) => void): Unsubscribe {
    this.stateCbs.add(cb);
    return () => this.stateCbs.delete(cb);
  }

  async stop() {
    if (this.stallTimer) clearInterval(this.stallTimer);
    this.unsub?.();
    this.unsub = null;
    try {
      if (this.hooks) await this.hooks.stop();
      else await this.client.cmd(this.deviceId, 'audio.listen.stop', { session_id: this.sessionId });
    } catch {
      /* best effort */
    }
    this.setState('stopped');
  }
}
